import { useEffect, useRef, useState } from "react";
import { useSpring, animated, to } from "@react-spring/web";
import { Info, SlidersHorizontal, Layers } from "lucide-react";
import { cn } from "@/lib/utils";
import { EDGE_TYPE_GROUPS } from "./org360Constants";

function DockTooltip({
  label,
  children,
  disabled,
}: {
  label: string;
  children: React.ReactNode;
  disabled?: boolean;
}) {
  const [visible, setVisible] = useState(false);

  const springs = useSpring({
    opacity: visible && !disabled ? 1 : 0,
    y: visible && !disabled ? 0 : 5,
    scale: visible && !disabled ? 1 : 0.88,
    config: { tension: 420, friction: 24 },
  });

  return (
    <div
      className="relative"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      {children}
      <animated.div
        className="absolute bottom-full left-1/2 mb-2.5 pointer-events-none z-50"
        style={{
          transform: to(
            [springs.y, springs.scale],
            (y, s) => `translateX(-50%) translateY(${y}px) scale(${s})`,
          ),
          opacity: springs.opacity,
        }}
      >
        <div className="whitespace-nowrap px-2.5 py-1 rounded-lg
          bg-foreground/90 text-background text-[11px] font-medium font-mono
          shadow-[0_2px_8px_hsl(var(--foreground)/0.15)]">
          {label}
        </div>
      </animated.div>
    </div>
  );
}

import type { Org360IncludeFlags } from "@/hooks/org360/useOrg360GraphData";

const NODE_CLASS_TOGGLES: { key: keyof Org360IncludeFlags; label: string; color: string }[] = [
  { key: "fields", label: "Fields", color: "#64748b" },
  { key: "reports", label: "Reports", color: "#a855f7" },
  { key: "managed", label: "Managed packages", color: "#f59e0b" },
];

interface GraphDockProps {
  contextOpen: boolean;
  inspectorOpen: boolean;
  onContext: () => void;
  onInspector: () => void;
  hiddenEdgeTypes: Set<string>;
  onToggleEdgeType: (key: string) => void;
  includeFlags: Org360IncludeFlags;
  onToggleInclude: (key: keyof Org360IncludeFlags) => void;
}

export function GraphDock({
  contextOpen,
  inspectorOpen,
  onContext,
  onInspector,
  hiddenEdgeTypes,
  onToggleEdgeType,
  includeFlags,
  onToggleInclude,
}: GraphDockProps) {
  const [edgeFilterOpen, setEdgeFilterOpen] = useState(false);
  const edgeFilterRef = useRef<HTMLDivElement>(null);

  const dockSpring = useSpring({
    from: { y: 40, opacity: 0 },
    to:   { y: 0,  opacity: 1 },
    config: { tension: 280, friction: 22 },
    delay: 400,
  });

  const popoverSpring = useSpring({
    opacity: edgeFilterOpen ? 1 : 0,
    y: edgeFilterOpen ? 0 : 8,
    scale: edgeFilterOpen ? 1 : 0.92,
    config: { tension: 380, friction: 26 },
  });

  useEffect(() => {
    if (!edgeFilterOpen) return;
    const handler = (e: MouseEvent) => {
      if (!edgeFilterRef.current?.contains(e.target as Node)) {
        setEdgeFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handler, true);
    return () => document.removeEventListener("mousedown", handler, true);
  }, [edgeFilterOpen]);

  const hasActiveFilter =
    hiddenEdgeTypes.size > 0 || NODE_CLASS_TOGGLES.some((t) => includeFlags[t.key]);

  return (
    <animated.div
      className="absolute bottom-5 left-1/2 z-20 inline-flex items-center gap-1 p-1
        border border-border rounded-full bg-card/90 backdrop-blur-md
        shadow-[0_4px_16px_hsl(var(--foreground)/0.08)]"
      style={{
        transform: dockSpring.y.to((v) => `translateX(-50%) translateY(${v}px)`),
        opacity: dockSpring.opacity,
      }}
    >
      <DockTooltip label="Org context">
        <button
          type="button"
          onClick={onContext}
          aria-label="Toggle org context"
          className={cn(
            "grid place-items-center w-9 h-9 rounded-full border-0 cursor-pointer",
            "text-muted-foreground transition-[background,color] duration-150",
            "hover:bg-muted hover:text-foreground",
            contextOpen && "bg-primary/12 text-primary",
          )}
        >
          <Info className="h-4 w-4" />
        </button>
      </DockTooltip>

      <div className="w-px h-5 bg-border mx-0.5" />

      {/* Edge type filter button + popover */}
      <div ref={edgeFilterRef} className="relative">
        <animated.div
          className="absolute bottom-full left-1/2 mb-3 z-50"
          style={{
            transform: to(
              [popoverSpring.y, popoverSpring.scale],
              (y, s) => `translateX(-50%) translateY(${y}px) scale(${s})`,
            ),
            opacity: popoverSpring.opacity,
            pointerEvents: edgeFilterOpen ? "auto" : "none",
          }}
        >
          <div className="w-52 rounded-xl border border-border bg-card/95 backdrop-blur-md
            shadow-[0_8px_24px_hsl(var(--foreground)/0.12)] p-1.5">
            <div className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground font-mono">
              Show in graph
            </div>
            {NODE_CLASS_TOGGLES.map((cls) => {
              const shown = !!includeFlags[cls.key];
              return (
                <button
                  key={cls.key}
                  type="button"
                  onClick={() => onToggleInclude(cls.key)}
                  title={`${shown ? "Hide" : "Show"} ${cls.label} (excluded by default)`}
                  className={cn(
                    "w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg cursor-pointer",
                    "text-[12px] font-mono transition-colors duration-100 hover:bg-muted",
                  )}
                >
                  <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: cls.color }} />
                  <span className={cn(
                    "flex-1 text-left transition-colors duration-100",
                    shown ? "text-foreground" : "text-muted-foreground/50",
                  )}>
                    {cls.label}
                  </span>
                  <span
                    className={cn(
                      "flex-shrink-0 w-3.5 h-3.5 rounded-sm border-[1.5px] flex items-center justify-center transition-all duration-100",
                      shown ? "border-transparent" : "border-border bg-transparent",
                    )}
                    style={shown ? { backgroundColor: cls.color } : {}}
                  >
                    {shown && (
                      <svg viewBox="0 0 10 10" width="8" height="8" fill="none">
                        <path d="M1.5 5.5l2.5 2.5 4.5-5" stroke="white" strokeWidth="1.5"
                          strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </span>
                </button>
              );
            })}
            <div className="my-1 h-px bg-border mx-1" />
            <div className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground font-mono">
              Edge Types
            </div>
            {EDGE_TYPE_GROUPS.map((group) => {
              const hidden = hiddenEdgeTypes.has(group.key);
              return (
                <button
                  key={group.key}
                  type="button"
                  onClick={() => onToggleEdgeType(group.key)}
                  className={cn(
                    "w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg cursor-pointer",
                    "text-[12px] font-mono transition-colors duration-100 hover:bg-muted",
                  )}
                >
                  <span
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ background: group.color }}
                  />
                  <span className={cn(
                    "flex-1 text-left transition-colors duration-100",
                    hidden ? "text-muted-foreground/50" : "text-foreground",
                  )}>
                    {group.label}
                  </span>
                  <span
                    className={cn(
                      "flex-shrink-0 w-3.5 h-3.5 rounded-sm border-[1.5px] flex items-center justify-center transition-all duration-100",
                      hidden ? "border-border bg-transparent" : "border-transparent",
                    )}
                    style={!hidden ? { backgroundColor: group.color } : {}}
                  >
                    {!hidden && (
                      <svg viewBox="0 0 10 10" width="8" height="8" fill="none">
                        <path
                          d="M1.5 5.5l2.5 2.5 4.5-5"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </animated.div>

        <DockTooltip label="Edge types" disabled={edgeFilterOpen}>
          <button
            type="button"
            onClick={() => setEdgeFilterOpen((v) => !v)}
            aria-label="Toggle edge type filters"
            className={cn(
              "grid place-items-center w-9 h-9 rounded-full border-0 cursor-pointer",
              "text-muted-foreground transition-[background,color] duration-150",
              "hover:bg-muted hover:text-foreground",
              (edgeFilterOpen || hasActiveFilter) && "bg-primary/12 text-primary",
            )}
          >
            <Layers className="h-4 w-4" />
          </button>
        </DockTooltip>
      </div>

      <div className="w-px h-5 bg-border mx-0.5" />

      <DockTooltip label="Node inspector">
        <button
          type="button"
          onClick={onInspector}
          aria-label="Toggle node inspector"
          className={cn(
            "grid place-items-center w-9 h-9 rounded-full border-0 cursor-pointer",
            "text-muted-foreground transition-[background,color] duration-150",
            "hover:bg-muted hover:text-foreground",
            inspectorOpen && "bg-primary/12 text-primary",
          )}
        >
          <SlidersHorizontal className="h-4 w-4" />
        </button>
      </DockTooltip>

    </animated.div>
  );
}
